import { useCallback, useEffect, useRef, useState } from "react";
import { BarChart3, Cookie, Lock, Mail } from "lucide-react";
import {
  acceptAll,
  getConsent,
  onOpenConsentPreferences,
  rejectAll,
  setConsent,
  type ConsentCategory,
} from "../../lib/consent";
import { useConsent } from "../../hooks/useConsent";

type Draft = Record<ConsentCategory, boolean>;

const categories: {
  key: ConsentCategory;
  icon: typeof Cookie;
  title: string;
  description: string;
}[] = [
  {
    key: "analytics",
    icon: BarChart3,
    title: "Analytics",
    description:
      "Google Analytics, so we can see which pages families actually find helpful and improve the ones they don't. Reported in aggregate — never used to identify you or your child.",
  },
  {
    key: "marketing",
    icon: Mail,
    title: "Marketing & Email",
    description:
      "Lets us follow up by email with practice news, developmental tips, and openings in the schedule, and lets us measure whether our ads are reaching the right families. Turn this off and you will not be added to any marketing email list.",
  },
];

export function ConsentBanner() {
  const { decided } = useConsent();

  const [rendered, setRendered] = useState(!decided);
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [draft, setDraft] = useState<Draft>({ analytics: false, marketing: false });

  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const seedDraft = useCallback(() => {
    const current = getConsent();
    setDraft({
      analytics: current?.analytics ?? false,
      marketing: current?.marketing ?? false,
    });
  }, []);

  // Reopened from a "Cookie Preferences" link — jump straight to the detail view.
  useEffect(
    () =>
      onOpenConsentPreferences(() => {
        clearTimeout(closeTimer.current);
        seedDraft();
        setShowDetails(true);
        setRendered(true);
      }),
    [seedDraft],
  );

  // Slide in on the frame after the element lands in the DOM.
  useEffect(() => {
    if (!rendered) return;
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, [rendered]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const dismiss = useCallback(() => {
    setVisible(false);
    closeTimer.current = setTimeout(() => {
      setRendered(false);
      setShowDetails(false);
    }, 250);
  }, []);

  // Esc collapses the detail view rather than dismissing — closing the banner
  // without a click would amount to consent nobody gave.
  useEffect(() => {
    if (!showDetails) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowDetails(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDetails]);

  useEffect(() => {
    if (rendered && visible) panelRef.current?.focus();
  }, [rendered, visible]);

  // The details view is a real modal (aria-modal + a backdrop that swallows
  // clicks), so Tab must not escape it. WCAG 2.1.2 No Keyboard Trap is about
  // the inverse — Esc and every button here still let the user out.
  useEffect(() => {
    if (!showDetails) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [showDetails]);

  if (!rendered) return null;

  const handleAcceptAll = () => {
    acceptAll();
    dismiss();
  };

  const handleRejectAll = () => {
    rejectAll();
    dismiss();
  };

  const handleSave = () => {
    setConsent(draft);
    dismiss();
  };

  const openDetails = () => {
    seedDraft();
    setShowDetails(true);
  };

  return (
    <>
      {showDetails && (
        <div
          className={`fixed inset-0 z-[190] bg-foreground/25 backdrop-blur-[2px] transition-opacity duration-250 ${
            visible ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden="true"
        />
      )}

      <div
        className="fixed inset-x-0 bottom-0 z-[200] flex justify-center p-3 sm:p-5 pointer-events-none"
        role="region"
        aria-label="Privacy consent"
      >
        <div
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-modal={showDetails ? true : undefined}
          aria-labelledby="consent-heading"
          aria-describedby="consent-description"
          className={`pointer-events-auto w-full ${
            showDetails ? "max-w-2xl" : "max-w-5xl"
          } max-h-[88vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-[0_18px_50px_-12px_rgba(10,31,61,0.35)] outline-none transition-all duration-250 ease-out ${
            visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
          }`}
        >
          <div className="p-5 sm:p-7">
            <div className="flex items-start gap-3.5">
              <span
                className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-accent"
                aria-hidden="true"
              >
                <Cookie className="h-5 w-5" />
              </span>

              <div className="min-w-0 flex-1">
                <h2
                  id="consent-heading"
                  className="font-display text-lg font-semibold text-foreground"
                >
                  {showDetails ? "Privacy preferences" : "Your privacy, your choice"}
                </h2>
                <p
                  id="consent-description"
                  className="mt-1.5 text-sm leading-relaxed text-muted-foreground"
                >
                  {showDetails ? (
                    <>
                      Choose what you are comfortable with. Necessary cookies keep the
                      site working; everything else is off until you say otherwise. Read
                      the{" "}
                      <a
                        href="/privacy/"
                        className="font-medium text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent"
                      >
                        Privacy Policy
                      </a>
                      .
                    </>
                  ) : (
                    <>
                      We use cookies to keep this site running, to understand how the
                      site is used and only with your permission to follow up by email.
                      Decline and we will not load Google Analytics or add you to any
                      marketing list. See our{" "}
                      <a
                        href="/privacy/"
                        className="font-medium text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent"
                      >
                        Privacy Policy
                      </a>
                      .
                    </>
                  )}
                </p>
              </div>
            </div>

            {showDetails && (
              <div className="mt-5 space-y-2.5 sm:pl-[54px]">
                <CategoryRow
                  icon={Lock}
                  title="Strictly Necessary"
                  description="Required for the site to function — page navigation, form security, and remembering the privacy choice you make right here. These cannot be switched off."
                  checked
                  locked
                />
                {categories.map(({ key, icon, title, description }) => (
                  <CategoryRow
                    key={key}
                    icon={icon}
                    title={title}
                    description={description}
                    checked={draft[key]}
                    onChange={(next) => setDraft((d) => ({ ...d, [key]: next }))}
                  />
                ))}
              </div>
            )}

            <div
              className={`mt-5 flex flex-col gap-2.5 sm:flex-row sm:items-center ${
                showDetails ? "sm:pl-[54px]" : "sm:justify-end sm:pl-[54px]"
              }`}
            >
              {showDetails ? (
                <>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="order-1 rounded-full bg-accent px-6 py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/85"
                  >
                    Save preferences
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="order-2 rounded-full border border-foreground/50 px-6 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                  >
                    Accept all
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="order-3 rounded-full border border-foreground/50 px-6 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                  >
                    Decline all
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={openDetails}
                    className="order-3 rounded-full px-4 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground sm:order-1 sm:mr-auto sm:px-2"
                  >
                    Customize
                  </button>
                  <button
                    type="button"
                    onClick={handleRejectAll}
                    className="order-2 rounded-full border border-foreground/50 px-6 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={handleAcceptAll}
                    className="order-1 rounded-full bg-accent px-6 py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/85 sm:order-3"
                  >
                    Accept all
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function CategoryRow({
  icon: Icon,
  title,
  description,
  checked,
  locked = false,
  onChange,
}: {
  icon: typeof Cookie;
  title: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-secondary/70 p-3.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      {locked ? (
        <span className="mt-0.5 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Always on
        </span>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          aria-label={`${title} cookies`}
          onClick={() => onChange?.(!checked)}
          className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${
            checked ? "bg-accent" : "bg-foreground/50"
          }`}
        >
          <span
            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
              checked ? "translate-x-[22px]" : "translate-x-0.5"
            }`}
          />
        </button>
      )}
    </div>
  );
}
